import { useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { hasTokens } from '../lib/auth'
import { listProfessionals, type Professional } from '../lib/api'
import { useMe } from '../hooks/useAuth'
import { canWriteProfessional } from '../lib/permissions'
import { ProfessionalModal, TIPI, TIPO_LABEL } from '../components/ProfessionalModal'

export const Route = createFileRoute('/professionals')({
  beforeLoad: () => {
    if (!hasTokens()) throw redirect({ to: '/login' })
  },
  component: ProfessionalsPage,
})

function ProfessionalsPage() {
  const qc = useQueryClient()
  const [fTipo, setFTipo] = useState('')
  const [fQ, setFQ] = useState('')
  const [modal, setModal] = useState<Professional | null | 'new'>()

  const { data: profs = [], isLoading } = useQuery({
    queryKey: ['professionals', fTipo, fQ],
    queryFn: () => listProfessionals({ tipo: fTipo || undefined, q: fQ || undefined }),
  })

  const { data: me } = useMe()
  const canWrite = canWriteProfessional(me)

  const invalidate = () => qc.invalidateQueries({ queryKey: ['professionals'] })

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Professionisti</h1>
        <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>delegati · custodi · notai · periti</span>
        {canWrite && (
          <button
            className="btn sm accent"
            style={{ marginLeft: 'auto' }}
            onClick={() => setModal('new')}
          >
            + Nuovo
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          placeholder="Cerca nome, email…"
          value={fQ}
          onChange={(e) => setFQ(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 'var(--r-md)', border: '1px solid var(--line)', fontSize: 13, width: 220, background: 'var(--bg-2)' }}
        />
        <select
          value={fTipo}
          onChange={(e) => setFTipo(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 'var(--r-md)', border: '1px solid var(--line)', fontSize: 13, background: 'var(--bg-2)' }}
        >
          <option value="">Tutti i tipi</option>
          {TIPI.map((t) => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div style={{ color: 'var(--ink-4)', padding: 20 }}>Caricamento…</div>
      ) : profs.length === 0 ? (
        <div style={{ color: 'var(--ink-4)', padding: 20, textAlign: 'center', fontStyle: 'italic' }}>
          Nessun professionista trovato.{' '}
          {canWrite && (
            <button onClick={() => setModal('new')} style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'inherit' }}>
              Creane uno.
            </button>
          )}
        </div>
      ) : (
        <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-2)', borderBottom: '1px solid var(--line)' }}>
                {['Tipo', 'Nome', 'Email / PEC', 'Telefono', ''].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-3)', fontWeight: 600 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {profs.map((p) => (
                <tr
                  key={p.id}
                  style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface)' }}
                >
                  <td style={{ padding: '8px 12px' }}>
                    <span className="chip sm" style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>
                      {TIPO_LABEL[p.tipo] ?? p.tipo}
                    </span>
                  </td>
                  <td style={{ padding: '8px 12px', fontWeight: 500, color: 'var(--ink-1)' }}>
                    {p.name}
                  </td>
                  <td style={{ padding: '8px 12px', color: 'var(--ink-2)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {p.email && <div>{p.email}</div>}
                    {p.pec && <div style={{ color: 'var(--ink-3)' }}>{p.pec}</div>}
                    {!p.email && !p.pec && <span style={{ color: 'var(--ink-4)' }}>—</span>}
                  </td>
                  <td style={{ padding: '8px 12px', color: 'var(--ink-2)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {p.telefono ?? <span style={{ color: 'var(--ink-4)' }}>—</span>}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    {canWrite && (
                      <button
                        onClick={() => setModal(p)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: 12, padding: '2px 8px' }}
                      >
                        Modifica
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal != null && (
        <ProfessionalModal
          initial={modal === 'new' ? null : modal}
          onClose={() => setModal(undefined)}
          onSaved={() => {
            setModal(undefined)
            invalidate()
          }}
        />
      )}
    </div>
  )
}
